import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCheck } from 'react-icons/fa';
import { useMobile } from '@/hooks/useMobile';
import { jobApplicationService } from '@/services/jobApplicationService';
import LoadingSpinner from '@/components/LoadingSpinner';
import toast from 'react-hot-toast';

interface Step {
  name: string;
  completed: boolean;
  date?: string;
}

interface Stage {
  name: string;
  steps: Step[];
}

interface StatusHistory {
  id: string;
  statusName: string;
  stage: number | null;
  stepName: string | null;
  createdAt: string;
}

const CandidateApplicationStatus: React.FC = () => {
  const { isMobile } = useMobile();
  const [loading, setLoading] = useState(true);
  const [stage1, setStage1] = useState<Stage>({
    name: 'Stage 1',
    steps: [
      { name: 'Application Submitted', completed: false },
      { name: 'Profile Screening', completed: false },
      { name: 'Shortlisted', completed: false },
      { name: 'Interview Scheduled', completed: false },
      { name: 'Selected', completed: false },
    ],
  });
  const [stage2, setStage2] = useState<Stage>({
    name: 'Stage 2',
    steps: [
      { name: 'Skill / Trade Test', completed: false },
      { name: 'Document Verification', completed: false },
      { name: 'Final Selection', completed: false },
      { name: 'Offer Letter Issued', completed: false },
      { name: 'Offer Accepted', completed: false },
      { name: 'Onboarding', completed: false },
    ],
  });

  useEffect(() => {
    loadApplicationStatus();
  }, []);

  const loadApplicationStatus = async () => {
    try {
      setLoading(true);
      // Get candidate email/mobile from localStorage
      const candidateEmail = localStorage.getItem('candidateEmail') || '';
      
      if (!candidateEmail) {
        toast.error('Please login to view your application status');
        return;
      }

      // Fetch application by email/mobile
      const application = await jobApplicationService.getApplicationByContact(candidateEmail);
      
      if (!application) {
        toast.error('Application not found');
        return;
      }

      // Fetch status history
      const statusHistory = await jobApplicationService.getStatusHistory(application.id);
      
      // Map status history to stages
      mapStatusHistoryToStages(statusHistory);
    } catch (error: any) {
      console.error('Error loading application status:', error);
      
      // Extract error message from backend response
      const errorMessage = error?.response?.data?.message 
        || error?.response?.data?.error 
        || error?.message 
        || 'Failed to load application status';
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const mapStatusHistoryToStages = (statusHistory: StatusHistory[]) => {
    // Define stage 1 steps
    const stage1Steps = [
      { name: 'Application Submitted', statusNames: ['Application Submitted'] },
      { name: 'Profile Screening', statusNames: ['Profile Screening', 'Reviewed'] },
      { name: 'Shortlisted', statusNames: ['Shortlisted'] },
      { name: 'Interview Scheduled', statusNames: ['Interview Scheduled'] },
      { name: 'Selected', statusNames: ['Selected'] },
    ];

    // Define stage 2 steps
    const stage2Steps = [
      { name: 'Skill / Trade Test', statusNames: ['Skill / Trade Test'] },
      { name: 'Document Verification', statusNames: ['Document Verification'] },
      { name: 'Final Selection', statusNames: ['Final Selection'] },
      { name: 'Offer Letter Issued', statusNames: ['Offer Letter Issued'] },
      { name: 'Offer Accepted', statusNames: ['Offer Accepted'] },
      { name: 'Onboarding', statusNames: ['Onboarding'] },
    ];

    // Map status history to stage 1 steps
    const mappedStage1 = stage1Steps.map(step => {
      const statusEntry = statusHistory.find(sh => 
        step.statusNames.some(sn => sh.statusName.toLowerCase().includes(sn.toLowerCase()) || 
        sh.stepName?.toLowerCase() === step.name.toLowerCase())
      );
      return {
        name: step.name,
        completed: !!statusEntry,
        date: statusEntry?.createdAt,
      };
    });

    // Map status history to stage 2 steps
    const mappedStage2 = stage2Steps.map(step => {
      const statusEntry = statusHistory.find(sh => 
        step.statusNames.some(sn => sh.statusName.toLowerCase().includes(sn.toLowerCase()) || 
        sh.stepName?.toLowerCase() === step.name.toLowerCase())
      );
      return {
        name: step.name,
        completed: !!statusEntry,
        date: statusEntry?.createdAt,
      };
    });

    setStage1({ name: 'Stage 1', steps: mappedStage1 });
    setStage2({ name: 'Stage 2', steps: mappedStage2 });
  };
  

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const renderStageMobile = (stage: Stage) => {
    const totalSteps = stage.steps.length;
    
    return (
      <div className="mb-6">
        <h3 className="text-base font-bold text-gray-900 mb-4">{stage.name}</h3>
        
        <div className="relative pl-4">
          {/* Vertical progress line */}
          {totalSteps > 1 && (
            <div className="absolute left-1.5 top-0 bottom-0 w-0.5 bg-blue-400 mt-1 mb-1"></div>
          )}
        
          {/* Steps */}
          <div className="space-y-4">
            {stage.steps.map((step, index) => (
                <div key={index} className="relative flex items-start">
                  {/* Step icon */}
                  <div className="absolute -left-3.5 w-3 h-3 flex items-center justify-center z-10 mt-1">
                    <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                  </div>
                  
                  {/* Step label */}
                  <div className="ml-2 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {step.name}
                    </p>
                    {step.date && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(step.date)}
                      </p>
                    )}
                  </div>
                </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderStageDesktop = (stage: Stage) => {
    const completedCount = stage.steps.filter(s => s.completed).length;
    const totalSteps = stage.steps.length;
    
    return (
      <div className="mb-4 sm:mb-5">
        <div className="flex items-center justify-between mb-4 sm:mb-5">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800">{stage.name}</h3>
          <span className="text-xs text-gray-500 font-medium">
            {completedCount}/{totalSteps} Completed
          </span>
        </div>
        
        <div className="relative">
          {/* Background progress line */}
          <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200 rounded-full mx-6 sm:mx-8"></div>
          
          {/* Completed progress line - dynamically calculated */}
          {completedCount > 0 && (() => {
            let lastCompletedIndex = -1;
            for (let i = stage.steps.length - 1; i >= 0; i--) {
              if (stage.steps[i].completed) {
                lastCompletedIndex = i;
                break;
              }
            }
            if (lastCompletedIndex === -1) return null;
            
            const stepWidth = 100 / totalSteps;
            const progressWidth = ((lastCompletedIndex + 1) / totalSteps) * 100;
            
            return (
              <div 
                className="absolute top-4 h-0.5 bg-blue-600 rounded-full transition-all duration-500"
                style={{ 
                  left: `${stepWidth / 2}%`,
                  width: `calc(${progressWidth}% - ${stepWidth}%)`,
                  maxWidth: 'calc(100% - 3rem)'
                }}
              ></div>
            );
          })()}
          
          {/* Steps */}
          <div className="relative flex justify-between items-start">
            {stage.steps.map((step, index) => {
              const isCompleted = step.completed;
              
              return (
                <div key={index} className="flex flex-col items-center flex-1 min-w-0 relative">
                  {/* Step icon container */}
                  <div className="relative z-10 mb-3 w-8 h-8 flex items-center justify-center">
                    {/* Step icon */}
                    <div className="w-8 h-8 rounded-full shadow-sm transition-all duration-300 bg-blue-600 flex items-center justify-center">
                      {isCompleted && (
                        <FaCheck className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>
                  
                  {/* Step label */}
                  <div className="text-center w-full px-0.5">
                    <p className="text-[10px] sm:text-xs font-medium leading-tight break-words text-gray-900">
                      {step.name}
                    </p>
                    {step.date && (
                      <p className="text-[9px] sm:text-[10px] text-gray-500 mt-1">
                        {formatDate(step.date)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Application Status - Candidate Dashboard</title>
      </Helmet>
      <div className="space-y-4 sm:space-y-6">

        {/* Status Section */}
        <div className="bg-white rounded-lg shadow-md border border-gray-100 p-4 sm:p-6">
          <div className="border-b border-gray-200 mb-4">
            <h1 className="text-base sm:text-lg font-bold text-gray-900 mb-1">
              Status of your Application
            </h1>
            {!isMobile && (
              <p className="text-xs sm:text-sm text-gray-600 mb-6">
                Track your application progress through each stage
              </p>
            )}
          </div>
          
          {/* Stage 1 */}
          <div className="mb-4 pb-4 border-b border-gray-200">
            {isMobile ? renderStageMobile(stage1) : renderStageDesktop(stage1)}
          </div>

          {/* Stage 2 */}
          <div>
            {isMobile ? renderStageMobile(stage2) : renderStageDesktop(stage2)}
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateApplicationStatus;

